import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
    PILL_BUTTON_BASE,
    PILL_BUTTON_CLASS,
    PILL_DIVIDER,
    PILL_MUTED_TEXT,
    PILL_NOTICE_SHADOW,
    PILL_NOTICE_SLEEVE,
    PILL_NOTICE_TEXT,
    PILL_SHADOW,
    PILL_SHEET_CSS,
    PILL_SHEET_ID,
    PILL_SURFACE,
    PILL_TRACK_CLASS,
    PILL_TRANSITION_MS,
    type SelectionBarCount,
    type SelectionBarEmphasis,
} from './selectionBarChrome';
import { useAdoptedStyleSheet } from './useAdoptedStyleSheet';

export type { SelectionBarCount, SelectionBarEmphasis };

export interface SelectionBarAction {
    label: string;
    onClick: () => void;
    /** 'primary' commits the pending change; 'secondary' (the default) is a filled chip;
     *  'quiet' is unfilled and takes a wash on hover. */
    emphasis?: SelectionBarEmphasis;
    icon?: React.ReactNode;
    iconPosition?: 'start' | 'end';
    disabled?: boolean;
    /** Renders a separator glyph before this action. */
    dividerBefore?: boolean;
}

export interface SelectionBarProps {
    /** Drives the mount: the bar animates in on true and animates out on false. */
    open: boolean;
    counts: SelectionBarCount[];
    actions: SelectionBarAction[];
    /** 'sticky' keeps the bar in view while its container scrolls; render it as the last child
     *  of that content, with no `overflow: hidden` on any ancestor. */
    placement?: 'sticky' | 'inline';
    /**
     * Wraps the bar in a warning sleeve carrying this message, for state the bar is
     * reporting rather than an action it offers — e.g. that the selection is unsaved.
     * It leads the announcement. An empty string renders no sleeve.
     */
    notice?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    className?: string;
}

/** A count reads as one phrase; an empty `value` carries a bare message instead. */
const countText = (count: SelectionBarCount): string =>
    String(count.value) === '' ? count.label : `${count.value} ${count.label}`;

const summarise = (counts: SelectionBarCount[]): string => counts.map(countText).join(', ');

/** A notice is the reason the bar is up, so it leads the announcement. */
const announcement = (notice: string | undefined, counts: SelectionBarCount[]): string =>
    notice === undefined || notice === ''
        ? summarise(counts)
        : `${notice} ${summarise(counts)}`.trim();

/**
 * A bar reporting a pending, not-yet-written selection and the actions that resolve it. Every
 * action destroys the button just pressed, so an `onClick` that ends the pending state must
 * also move focus.
 */
export const SelectionBar: React.FC<SelectionBarProps> = ({
    open,
    counts,
    actions,
    placement = 'sticky',
    notice,
    'aria-label': ariaLabel = 'Pending changes',
    'aria-labelledby': ariaLabelledBy,
    className = '',
}) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [isMounted, setIsMounted] = useState(open);
    const [isVisible, setIsVisible] = useState(open);
    const frameRef = useRef<number | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useAdoptedStyleSheet(trackRef, PILL_SHEET_CSS, PILL_SHEET_ID);

    useEffect(() => {
        if (open) {
            setIsMounted(true);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            frameRef.current = requestAnimationFrame(() => {
                frameRef.current = null;
                setIsVisible(true);
            });
        } else {
            // The browser refuses `aria-hidden` on a subtree still holding focus, so a caller
            // that forgets to move it would trap the keyboard here.
            const active = document.activeElement;
            if (active instanceof HTMLElement && trackRef.current?.contains(active)) {
                active.blur();
            }
            setIsVisible(false);
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                setIsMounted(false);
            }, PILL_TRANSITION_MS);
        }
        return () => {
            if (frameRef.current !== null) {
                cancelAnimationFrame(frameRef.current);
                frameRef.current = null;
            }
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [open]);

    const isSticky = placement === 'sticky';
    const hasNotice = notice !== undefined && notice !== '';

    const pill = (
        <div className={PILL_SURFACE} style={{ boxShadow: PILL_SHADOW }}>
            {counts.length > 0 && (
                <p
                    aria-hidden="true"
                    className="m-0 flex items-center gap-2 text-[13px] leading-[1.6] tracking-[0.13px]"
                >
                    {counts.map((count, index) => (
                        <Fragment key={count.label}>
                            {index > 0 && <span className={PILL_MUTED_TEXT}>•</span>}
                            <span>{countText(count)}</span>
                        </Fragment>
                    ))}
                </p>
            )}
            {actions.map((action) => (
                <Fragment key={action.label}>
                    {action.dividerBefore && (
                        <span aria-hidden="true" className={`${PILL_DIVIDER} ${PILL_MUTED_TEXT}`}>
                            |
                        </span>
                    )}
                    <button
                        type="button"
                        disabled={action.disabled}
                        tabIndex={isVisible ? undefined : -1}
                        onClick={action.onClick}
                        className={`${PILL_BUTTON_BASE} ${PILL_BUTTON_CLASS}--${action.emphasis ?? 'secondary'}`}
                    >
                        {action.icon && action.iconPosition !== 'end' && (
                            <span aria-hidden="true" className="flex items-center">
                                {action.icon}
                            </span>
                        )}
                        {action.label}
                        {action.icon && action.iconPosition === 'end' && (
                            <span aria-hidden="true" className="flex items-center">
                                {action.icon}
                            </span>
                        )}
                    </button>
                </Fragment>
            ))}
        </div>
    );

    return (
        // The live region has to exist BEFORE its text changes or the first announcement is dropped.
        <div
            ref={trackRef}
            className={`${PILL_TRACK_CLASS} pointer-events-none flex w-full justify-center ${isSticky ? 'sticky bottom-6 z-30' : 'relative'} ${className}`.trim()}
            style={{ opacity: isVisible ? 1 : 0 }}
        >
            <p className="sr-only" role="status">
                {isVisible ? announcement(notice, counts) : ''}
            </p>
            {isMounted && (
                <div
                    role="group"
                    aria-label={ariaLabelledBy ? undefined : ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    aria-hidden={isVisible ? undefined : true}
                    className={isVisible ? 'pointer-events-auto' : undefined}
                >
                    {/* Keep the wrapper stable: changing the pill's parent remounts it and drops focus
     from the action that raised the notice. */}
                    <div
                        className={hasNotice ? PILL_NOTICE_SLEEVE : undefined}
                        style={hasNotice ? { boxShadow: PILL_NOTICE_SHADOW } : undefined}
                    >
                        {hasNotice && (
                            <p aria-hidden="true" className={PILL_NOTICE_TEXT}>
                                {notice}
                            </p>
                        )}
                        {pill}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SelectionBar;
